import {createSlice, nanoid, PayloadAction} from '@reduxjs/toolkit';
import path from 'path';
import slash from 'slash';
import {
  e57FileFormat,
  encompassFileFormat,
  potreeFileFormat,
  lasFileFormat
} from '../file-extensions';
import { TagSize } from '../types/tags';
import { SceneCameraState } from './camera-slice';

export interface PlanarConfig {
  width: number;
  height: number;
  cx: number;
  cy: number;
  fx: number;
  fy: number;
}

export interface CameraData {
  id: string;
  x: number;
  y: number;
  z: number;
  roll: number;
  pitch: number;
  yaw: number;
  path: string;
  name: string;
  config?: PlanarConfig;
}

export interface OrthoData {
  imageBounds: [];
  imageDataSplit: [];
  imageData: string;
  imageSize: [number, number];
  imageSizeOriginal: [number, number];
  imagePath: string;
  scaleX: number;
  scaleY: number;
}

export interface TagItemData {
  id: string;
  name: string;
  comment: string;
  date: string;
  x: number;
  y: number;
  z?: number;
  sceneState: SceneCameraState;
}

export interface TagData {
  size: TagSize;
  texture: string;
  items: TagItemData[];
}

export interface LayerData {
  id: string;
  name: string;
  visible: boolean;
  color: string;
}

interface XMLAlignment {
  id: string;
  name: string;
  length: number;
  staStart: number;
  about: string;
  geometry: object[];
}

export interface XMLData {
  info: object;
  units: object;
  alignments: XMLAlignment[];
  enu: boolean;
}

type assetData = OrthoData | CameraData[] | TagData | LayerData[] | XMLData

export interface Asset {
  id: string;
  name: string;
  path: string;
  saved: boolean;
  cloud: boolean;
  date: string;
  visible: boolean;
  folderID: string;
  type: AssetType;
  data: assetData;
}

export interface AssetListItem {
  folderID: string;
  assetPath: string;
  type: AssetType;
  data?: assetData;
}

export enum AssetType {
  LAS = 0,
  Encompass = 1,
  Panoramic = 2,
  Unknown = 3,
  OrthoMosaic = 4,
  Planar = 5,
  E57Points = 6,
  Tag = 7,
  IFC = 8,
  Potree = 9,
  SHP = 10,
  DXF = 11,
  LandXML = 12
}

export const pointCloudType = (ext: string): AssetType => {
  if (ext === lasFileFormat) {
    return AssetType.LAS;
  } else if (ext === encompassFileFormat) {
    return AssetType.Encompass;
  } else if (ext === e57FileFormat) {
    return AssetType.E57Points;
  } else if (ext === potreeFileFormat) {
    return AssetType.Potree;
  } else {
    return AssetType.Unknown;
  }
};

export const isCameraOrTag = (assetType) => {
  return (assetType === AssetType.Tag)
    || (assetType === AssetType.Planar)
    || (assetType === AssetType.Panoramic);
};

const getAssetPayload = (folderID: string, assetPath: string, type: AssetType, data?: assetData): Asset => {
  const cleanAssetPath = slash(assetPath);
  const assetName = path.basename(cleanAssetPath);

  return {
    id: nanoid(),
    name: assetName,
    path: cleanAssetPath,
    date: new Date().toISOString(),
    type,
    folderID,
    visible: true,
    saved: false,
    cloud: false,
    data
  } as Asset;
};

export const assetsSlice = createSlice({
  name: 'assets',
  initialState: [] as Asset[],
  reducers: {
    addAsset: {
      reducer: (state, action: PayloadAction<Asset>) => {
        state.push(action.payload);
      },
      prepare: (folderID: string, assetPath: string, type: AssetType, data?: assetData): {payload: Asset} =>
        ({payload: getAssetPayload(folderID, assetPath, type, data)})
    },
    addAssets: {
      /** Add multiple assets at once. Much faster than adding single assets */
      reducer: (state, action: PayloadAction<Asset[]>) => {
        action.payload.forEach(payload => {
          state.push(payload);
        });
      },
      prepare: (assetsList: AssetListItem[]): {payload: Asset[]} => {
        const payload = assetsList.map(assetItem => {
          const {folderID, assetPath, type, data} = assetItem;
          return getAssetPayload(folderID, assetPath, type, data);
        });

        return {payload};
      }
    },
    addNewTag: (state, action) => {
      const {assetID, newTag} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      const items = (asset.data as TagData).items;

      asset.data = {
        ...asset.data,
        items: [...items, newTag]
      } as TagData;
    },
    updateTagContent: (state, action: PayloadAction<{
      tagID: string,
      name?: string,
      comment?: string,
      sceneState?: SceneCameraState
    }>) => {
      const {tagID, comment, name, sceneState} = action.payload;

      state.forEach(asset => {
        if (asset.type !== AssetType.Tag) return;

        const items = (asset.data as TagData).items;
        const tag = items.find(tag => tag.id === tagID);
        if (!tag) return;

        if ("name" in action.payload) {
          tag.name = name;
        }

        if ("comment" in action.payload) {
          tag.comment = comment;
        }

        if ("sceneState" in action.payload) {
          tag.sceneState = sceneState;
        }

        tag.date = new Date().toISOString();
      });
    },
    changeTagTexture: (state, action: PayloadAction<{texture: string, assetID: string}>) => {
      const {assetID, texture} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      const tags = asset.data as TagData;
      tags.texture = texture;
    },
    changeTagSize: (state, action: PayloadAction<{size: TagSize, assetID: string}>) => {
      const {assetID, size} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      const tags = asset.data as TagData;
      tags.size = size;
    },
    changeFolder: (state, action: PayloadAction<{folderID: string, assetID: string}>) => {
      const {assetID, folderID} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.folderID = folderID;
    },
    updateAssetName: (state, action) => {
      const {assetID, name} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.name = name;
    },
    toggleVisibility: (state, action) => {
      const assetID = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.visible = !asset.visible;
    },
    toggleXYSwitch: (state, action) => {
      const assetID = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      if (asset.type !== AssetType.LandXML) return;
      const data = asset.data as XMLData;
      const enu = data.enu;
      asset.data = {...asset.data, enu: !enu};
    },
    toggleFolderVisibility: (state, action) => {
      const {folderID, visible} = action.payload;
      const assets = state.filter(asset => asset.folderID === folderID);

      assets.forEach(asset => {
        asset.visible = visible;
      });
    },
    setVisibility: (state, action) => {
      const {assetID, visible} = action.payload;
      const asset = state.find(asset => asset.id === assetID);
      if (!asset) return;

      asset.visible = visible;
    },
    setVisibleAssets: (state, action) => {
      const visibleAssets = action.payload;
      state.forEach(asset => {
        asset.visible = visibleAssets.includes(asset.id);
      });
    },
    setVisibleFolder: (state, action) => {
      const folderID = action.payload;
      state.forEach(asset => {
        asset.visible = asset.folderID === folderID;
      });
    },
    setSavedState: (state, action) => {
      state.forEach(asset => {
        asset.saved = true;
      });
    },
    deleteTagData: (state, action) => {
      state.forEach(asset => {
        if (asset.type !== AssetType.Tag) return;

        const {items} = asset.data as TagData;
        const filtered = items.filter(item => {
          return !action.payload.includes(item.id);
        });

        asset.data = {
          ...asset.data,
          items: filtered
        } as TagData;
      });
    },
    deleteAsset: (state, action) =>
      state.filter(asset => asset.id !== action.payload),
    deleteAssets: (state, action) =>
      state.filter(asset => asset.folderID !== action.payload)
  }
});

export default assetsSlice.reducer;

export const {
  addAsset,
  addAssets,
  changeFolder,
  addNewTag,
  updateTagContent,
  changeTagTexture,
  changeTagSize,
  setVisibleAssets,
  toggleVisibility,
  toggleXYSwitch,
  toggleFolderVisibility,
  setVisibility,
  setVisibleFolder,
  setSavedState,
  updateAssetName,
  deleteTagData,
  deleteAsset,
  deleteAssets,
} = assetsSlice.actions;

export const selectAllAssets = state => {
  return state.assets as Asset[];
};

export const getModelAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return (asset.type === AssetType.IFC)
      || (asset.type === AssetType.SHP)
      || (asset.type === AssetType.DXF);
  });
};

export const getTagAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.Tag;
  });
};

export const getPointCloudAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return (asset.type === AssetType.Encompass)
      || (asset.type === AssetType.LAS)
      || (asset.type === AssetType.E57Points)
      || (asset.type === AssetType.Potree);
  });
};

export const getCameraFileAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.Panoramic
      || asset.type === AssetType.Planar;
  });
};

export const getCameraListFromAssets = (assets: Asset[], checkVisible: boolean) => {
  const cameraAssets = getCameraFileAssets(assets);

  return cameraAssets.map(cameraFile => {
    if (checkVisible && !cameraFile.visible) return [];

    const items = cameraFile.data as CameraData[];

    return items.map(camera => {
      return {
        ...camera,
        csv: cameraFile.id,
        folder: cameraFile.folderID,
        assetType: cameraFile.type,
        saved: cameraFile.saved,
      };
    });
  }).flat();
};

export const getOrthoAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.OrthoMosaic;
  });
};

export const getLandXMLAssets = (assets: Asset[]) => {
  return assets.filter(asset => {
    return asset.type === AssetType.LandXML;
  });
};

